<div class="blackout-window-panel" data-testid="scoring-window-blackout-panel">

  <h3>Non-working hours</h3>
  <p class="blackout-note">
    Blocks the selected scorer activity during non-working hours. Scorers can still sign in and
    see their dashboard, and batches they already claimed are held rather than released.
  </p>

  <div *ngIf="isLoading" class="notification is-light">Loading non-working hours...</div>

  <div *ngIf="loadError" class="notification is-warning" data-testid="blackout-load-error">
    {{ loadError }}
  </div>

  <div *ngIf="!isLoading && !loadError && schedule">

    <div
      class="notification is-warning"
      *ngIf="!schedule.is_schema_ready"
      data-testid="blackout-schema-not-ready"
    >
      Non-working hours cannot be configured on this environment yet — the supporting database
      tables have not been deployed. Scoring stays available at all times until they are.
    </div>

    <section class="blackout-section blackout-assign-section" data-testid="blackout-assign-section">
      <h4>
        Assign to scoring window
        <a
          *ngIf="showTcWindowLink && markingWindowLink(markingWindowId) as tcLink"
          [routerLink]="tcLink.routerLink"
          [queryParams]="tcLink.queryParams"
        ><code>{{ markingWindowId }}</code></a>
        <code *ngIf="!showTcWindowLink">{{ markingWindowId }}</code>
      </h4>
      <p class="blackout-note">{{ assignmentStatusLabel }}</p>

      <div
        class="notification is-success is-light blackout-assignment-applied"
        *ngIf="isAttachmentUnchanged && activeGroups.length"
        data-testid="blackout-assignment-applied"
      >
        <span *ngIf="activeGroups.length === 1">
          <strong>{{ activeGroups[0].name }}</strong> is already attached.
        </span>
        <span *ngIf="activeGroups.length > 1">
          <strong>{{ activeGroups.length }} groups</strong> are already attached; their rules are
          combined, so scoring is blocked whenever any of them blocks it.
        </span>
        Check or uncheck groups below to change the set.
      </div>

      <div class="field">
        <label class="label is-small">Non-working hour groups</label>
        <blackout-hours-group-picker
          [groups]="groups"
          [selectedGroupIds]="selectedGroupIds"
          [activeGroupIds]="activeGroupIds"
          [isCreatingGroup]="isCreatingGroup"
          [editingGroupId]="editingGroupId"
          [markingWindowId]="markingWindowId"
          [disabled]="isReadOnly || isApplying || isSaving"
          (selectedGroupIdsChange)="onSelectedGroupIdsChange($event)"
          (editGroup)="onEditGroupRequested($event)"
          (createGroup)="startCreateGroup()"
          (clearSelection)="clearGroupSelection()"
        ></blackout-hours-group-picker>
      </div>

      <p class="blackout-note blackout-pending-assign" *ngIf="pendingAssignmentLabel" data-testid="blackout-pending-assignment">
        {{ pendingAssignmentLabel }}
      </p>

      <p
        class="blackout-note blackout-selection-scope"
        *ngIf="sharedWindowsForSelection.length"
        data-testid="blackout-selection-scope"
      >
        The checked groups are also used by
        {{ sharedWindowsForSelection.length }} other scoring window{{ sharedWindowsForSelection.length === 1 ? '' : 's' }}
        — attaching them here does not change those windows, but editing their rules does.
      </p>

      <div class="buttons">
        <button
          class="button is-small is-link"
          [class.is-loading]="isApplying"
          [disabled]="!canApplySchedule"
          (click)="applySchedule()"
          data-testid="blackout-apply-schedule"
        >Apply to scoring window</button>
        <button
          class="button is-small"
          [disabled]="isReadOnly || isApplying || isSaving"
          (click)="cancel()"
          data-testid="blackout-cancel-assignment"
        >Reset selection</button>
      </div>

      <div class="field blackout-resume-flag" data-testid="blackout-resume-flag">
        <label class="checkbox">
          <input
            type="checkbox"
            [checked]="isResumeTimeHidden"
            [disabled]="isReadOnly || isSavingResumeFlag || !isResumeTimeFlagAvailable"
            (change)="onResumeFlagChange($event)"
            data-testid="blackout-resume-flag-toggle"
          />
          Hide from scorers when non-working hours end
        </label>
        <p class="blackout-note">
          Scorers see that a task is in non-working hours but not the time it opens again,
          and the schedule preview is hidden. Saves immediately.
        </p>
        <p class="blackout-note" *ngIf="!isResumeTimeFlagAvailable">
          Not available on this environment yet — the supporting database column has not been deployed.
        </p>
      </div>
    </section>

    <div *ngIf="isEditingRules" class="blackout-rules-workspace">

      <div
        class="notification is-warning blackout-scope-warning"
        *ngIf="sharedWindows.length"
        data-testid="blackout-scope-warning"
      >
        <strong>These rules are shared.</strong>
        Saving them also changes non-working hours for
        {{ sharedWindows.length }} other scoring window{{ sharedWindows.length === 1 ? '' : 's' }}:
        <ng-container *ngFor="let win of sharedWindows; let last = last">
          <a
            *ngIf="scopeWindowLink(win.id) as scopeLink"
            [routerLink]="scopeLink.routerLink"
            [queryParams]="scopeLink.queryParams"
          >{{ markingWindowLabel(win) }}</a>
          <span *ngIf="!scopeWindowLink(win.id)">{{ markingWindowLabel(win) }}</span><span *ngIf="!last">, </span>
        </ng-container>.
        To change this window only, create a separate group instead.
      </div>

      <div class="blackout-toolbar">
        <div class="blackout-toolbar-left">
          <strong>{{ isCreatingGroup ? 'New group rules' : (editingGroup?.name || 'Group rules') }}</strong>
          <span class="blackout-toolbar-hint" *ngIf="!isCreatingGroup && selectedGroupIds.length > 1">
            editing 1 of {{ selectedGroupIds.length }} checked groups
          </span>
        </div>
        <div class="blackout-toolbar-center">
          <button class="button is-small is-link is-light" [disabled]="isReadOnly || isApplying || isSaving" (click)="addRecurringRule()" data-testid="blackout-add-recurring">+ Weekly rule</button>
          <button class="button is-small is-link is-light" [disabled]="isReadOnly || isApplying || isSaving" (click)="addDateRule()" data-testid="blackout-add-date">+ Specific date</button>
        </div>
        <div class="blackout-toolbar-right">
          <button
            class="button is-small is-link"
            [class.is-loading]="isSaving"
            [disabled]="!canSaveRules"
            (click)="saveGroupRules()"
            data-testid="blackout-save-rules"
          >{{ isCreatingGroup ? 'Save group' : 'Save group rules' }}</button>
        </div>
      </div>

      <p class="blackout-error" *ngIf="isCreatingGroup && createValidationError" data-testid="blackout-create-validation-error">{{ createValidationError }}</p>
      <p class="blackout-error" *ngIf="!isCreatingGroup && rulesValidationError" data-testid="blackout-rules-validation-error">{{ rulesValidationError }}</p>

      <div class="blackout-create-meta" *ngIf="isCreatingGroup" data-testid="blackout-create-group-section">
        <div class="field">
          <label class="label is-small">Group name</label>
          <input class="input is-small blackout-name-input" type="text" [disabled]="isReadOnly || isApplying || isSaving" [(ngModel)]="newGroupName" (ngModelChange)="syncPreviewGroups()" placeholder="e.g. PJ 2026 overnight blackout" data-testid="blackout-new-group-name" />
        </div>
        <div class="field">
          <label class="label is-small">Timezone</label>
          <div class="select is-small">
            <select [disabled]="isReadOnly || isApplying || isSaving" [(ngModel)]="newGroupTimezone" (ngModelChange)="syncPreviewGroups()" data-testid="blackout-new-group-timezone">
              <option *ngFor="let tz of TIMEZONE_OPTIONS" [ngValue]="tz">{{ tz }}</option>
            </select>
          </div>
        </div>
        <div class="field">
          <label class="label is-small">Academic year</label>
          <input class="input is-small blackout-year-input" type="text" [disabled]="isReadOnly || isApplying || isSaving" [(ngModel)]="newGroupAcademicYear" placeholder="e.g. 2025-2026" data-testid="blackout-new-group-academic-year" />
          <p class="blackout-note" *ngIf="windowAcademicYear">
            Defaults to <strong>{{ windowAcademicYear }}</strong>, this scoring window's year. Groups from any
            year can still be assigned with the picker above.
          </p>
        </div>
        <p class="blackout-note">
          Add rules, then click <strong>Save group</strong> to save it without attaching, or
          <strong>Apply to scoring window</strong> above to save and attach it in one step.
        </p>
      </div>

      <div class="blackout-edit-meta" *ngIf="editingGroupId > 0 && !isCreatingGroup" data-testid="blackout-rules-section">
        <p class="blackout-note" *ngIf="!isAttachmentUnchanged">
          This group is checked but not attached to scoring window {{ markingWindowId }} yet.
          Click <strong>Apply to scoring window</strong> above.
        </p>
        <div class="field">
          <label class="label is-small">Timezone</label>
          <div class="select is-small">
            <select [disabled]="isReadOnly || isApplying || isSaving" [(ngModel)]="editTimezone" (ngModelChange)="syncPreviewGroups()" data-testid="blackout-timezone">
              <option *ngFor="let tz of TIMEZONE_OPTIONS" [ngValue]="tz">{{ tz }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="blackout-preview-panel">
        <blackout-hours-schedule-preview
          [groups]="previewGroups"
          [timezone]="isCreatingGroup ? newGroupTimezone : editTimezone"
          [isScheduleOn]="previewScheduleOn"
          [contextKey]="previewContextKey"
        ></blackout-hours-schedule-preview>
      </div>

      <div class="blackout-two-col">
        <section class="blackout-col blackout-col-rules">
          <h4 class="blackout-col-title">Group rules</h4>
          <blackout-hours-rules-list
            [ranges]="editRanges"
            [selectedIndex]="selectedRuleIndex"
            [scopePickerOptions]="schedule?.scopePickerOptions"
            [disabled]="isReadOnly || isApplying || isSaving"
            (selectedIndexChange)="onSelectedRuleIndexChange($event)"
            (duplicateRule)="duplicateRule($event)"
            (revokeRule)="revokeRule($event)"
          ></blackout-hours-rules-list>
        </section>

        <section class="blackout-col blackout-col-config">
          <h4 class="blackout-col-title">Rule config</h4>
          <div *ngIf="selectedRuleIndex == null" class="notification is-light is-size-7">
            Select a rule from the list, or add one with the toolbar above.
          </div>
          <blackout-hours-rule-form
            *ngIf="selectedRuleIndex != null && editRanges[selectedRuleIndex]"
            [range]="editRanges[selectedRuleIndex]"
            [index]="selectedRuleIndex"
            [timezone]="isCreatingGroup ? newGroupTimezone : editTimezone"
            [disabled]="isReadOnly || isApplying || isSaving"
            [dateSectionOpen]="isDateSectionOpen(selectedRuleIndex)"
            [scopePickerOptions]="schedule?.scopePickerOptions"
            (rangeChange)="onRuleChange(selectedRuleIndex, $event)"
            (dateSectionOpenChange)="setDateSectionOpen(selectedRuleIndex, $event)"
          ></blackout-hours-rule-form>
          <div class="blackout-rule-actions" *ngIf="selectedRuleIndex != null">
            <button
              type="button"
              class="button is-small is-link"
              [class.is-loading]="isSaving"
              [disabled]="!canSaveRule"
              (click)="saveRule()"
              data-testid="blackout-rule-save"
            >Save rule</button>
            <button
              type="button"
              class="button is-small"
              [disabled]="isSaving || isApplying || isReadOnly || !isSelectedRuleDirty"
              (click)="resetSelectedRule()"
              data-testid="blackout-rule-reset"
            >Reset</button>
            <p class="blackout-rule-actions-hint" *ngIf="isCreatingGroup">
              Rule changes are included when you save or apply the new group.
            </p>
            <p class="blackout-rule-actions-hint" *ngIf="!isCreatingGroup && !isSelectedRuleDirty && !isSaving">
              Rule saved.
            </p>
          </div>
        </section>
      </div>
    </div>

    <div class="buttons blackout-actions">
      <button class="button is-small is-text" (click)="toggleHistory()" data-testid="blackout-history-toggle">
        {{ isShowHistory ? 'Hide change history' : 'Show change history' }}
      </button>
    </div>

    <div *ngIf="isShowHistory" class="blackout-history" data-testid="blackout-history">
      <table class="table is-narrow is-fullwidth">
        <thead>
          <tr>
            <th>Group</th>
            <th>Attached</th>
            <th>Detached</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let link of schedule.history">
            <td>{{ link.group_name || link.mw_disallowed_time_range_group_id }}</td>
            <td>{{ link.created_on }}</td>
            <td>{{ link.is_revoked ? link.revoked_on : '—' }}</td>
          </tr>
          <tr *ngIf="!schedule.history?.length">
            <td colspan="3">No attachment history yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</div>
